'use client';
import React, { useCallback, useEffect } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';

interface Props {
  classes?: any;
  title: string;
  children: React.ReactNode;
  titleVariant?: React.ElementType;
  titleClassName?: string;
  onClick?: () => void;
  className?: string;
  dark?: boolean;
  isOpen?: boolean;
}

const BaseAccordion = ({
  classes,
  title,
  children,
  titleVariant: TitleTag = 'h5',
  titleClassName,
  onClick,
  className,
  dark = false,
  isOpen = false,
}: Props) => {
  const [expanded, setExpanded] = React.useState(!!isOpen);

  useEffect(() => {
    setExpanded(!!isOpen);
  }, [isOpen]);

  const internalOnClick = useCallback(() => {
    if (onClick) onClick();
    else setExpanded((ex) => !ex);
  }, [onClick]);

  const textColor = dark ? 'tw-text-neutral-50' : 'tw-text-neutral-500';

  return (
    <div
      className={classNames(
        'tw-border-t tw-border-neutral-200 tw-transition-all tw-duration-300',
        expanded ? 'tw-pb-4' : 'tw-pb-0',
        className || classes?.accordion,
      )}
    >
      <TitleTag
        className={classNames(
          'tw-text-left',
          textColor,
          titleClassName,
        )}
      >
        <Button
          variant={ButtonVariant.bare}
          size="none"
          className={classNames(
            'tw-flex tw-justify-between tw-items-center tw-py-3 tw-cursor-pointer tw-w-full',
            classes?.accordionSummary,
            expanded && classes?.accordionSummaryExpanded,
          )}
          onClick={internalOnClick}
          aria-controls={`${title}-content`}
          id={title}
          role="button"
          aria-expanded={expanded}
        >
          {title}

          <ChevronIcon
            className={classNames(
              'tw-w-6 tw-h-6 tw-max-w-5 tw-transform tw-transition-transform tw-duration-300',
              expanded ? 'tw-rotate-0' : 'tw-rotate-180',
              textColor,
            )}
          />
        </Button>
      </TitleTag>

      <div
        className={classNames(
          'tw-overflow-hidden tw-transition-all tw-duration-300 tw-pl-1',
          expanded
            ? 'tw-max-h-screen tw-opacity-100 tw-py-3'
            : 'tw-max-h-0 tw-opacity-0 tw-pointer-events-none',
          classes?.accordionDetails,
        )}
        id={`${title}-content`}
        style={{ visibility: !expanded ? 'hidden' : 'visible' }}
        aria-hidden={!expanded}
      >
        {children}
      </div>
    </div>
  );
};

export default BaseAccordion;
